import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDataMode } from '../../contexts/DataModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  FileText, Inbox, CheckCircle2, Calendar, Plus,
  Clock, Building2, DollarSign, ChevronRight,
  AlertTriangle, TrendingUp, Sparkles, User2, ArrowRight,
  Activity, X, Flame, Star, Globe, MapPin, MessageSquare, Users
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import TestimonialForm from '../../components/TestimonialForm';
import GlobalPresenceWidget from './GlobalPresenceWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOCK_DATA = {
  stats: { activeBriefs: 3, receivedProposals: 12, awardedProjects: 2, totalBudget: 185000 },
  recentBriefs: [
    { id: 'br_001', title: 'Istanbul Tech Fair 2025 — Stand Design', status: 'published', proposals: 4, budget_max: 45000, currency: 'TRY', deadline: '2025-02-15', city: 'Istanbul' },
    { id: 'br_002', title: 'Hannover Messe — Modular Stand', status: 'awarded', proposals: 7, budget_max: 82000, currency: 'EUR', deadline: '2025-03-01', city: 'Hannover' },
    { id: 'br_003', title: 'Dubai Expo Corporate Pavilion', status: 'draft', proposals: 0, budget_max: 58000, currency: 'USD', deadline: '2025-04-10', city: 'Dubai' },
  ],
  recentProposals: [
    { id: 'pr_001', contractor: 'Istanbul Fair Solutions', brief: 'Istanbul Tech Fair 2025', amount: 42500, currency: 'TRY', status: 'pending', submitted: '2025-01-08' },
    { id: 'pr_002', contractor: 'Berlin Stand GmbH', brief: 'Hannover Messe Stand', amount: 79000, currency: 'EUR', status: 'accepted', submitted: '2025-01-05' },
    { id: 'pr_003', contractor: 'Global Expo Builders', brief: 'Istanbul Tech Fair 2025', amount: 48000, currency: 'TRY', status: 'pending', submitted: '2025-01-07' },
  ],
  upcomingFairs: [
    { name: 'Tech Istanbul 2025', date: 'Feb 15, 2025', location: 'CNR Expo, Istanbul', status: 'Preparation' },
    { name: 'Hannover Messe 2025', date: 'Mar 1, 2025', location: 'Hannover, Germany', status: 'Quote Received' },
  ],
};

const STATUS_CONFIG = {
  draft:     { color: 'bg-gray-100 text-gray-600',       label: 'Draft' },
  published: { color: 'bg-blue-100 text-blue-700',       label: 'Published' },
  awarded:   { color: 'bg-emerald-100 text-emerald-700', label: 'Awarded' },
  closed:    { color: 'bg-red-100 text-red-600',         label: 'Closed' },
  pending:   { color: 'bg-yellow-100 text-yellow-700',   label: 'Pending' },
  accepted:  { color: 'bg-emerald-100 text-emerald-700', label: 'Accepted' },
  rejected:  { color: 'bg-red-100 text-red-600',         label: 'Rejected' },
};

const MOCK_RECOMMENDED = [
  { org_id: 'org_c1', name: 'Istanbul Fair Solutions', country: 'TR', city: 'Istanbul', tagline: 'Award-winning stand design since 2008', services: ['Stand Design', 'Modular Systems', 'Lighting'], coverage_count: 12, profile_score: 92, proposal_count: 47 },
  { org_id: 'org_c2', name: 'Berlin Stand GmbH', country: 'DE', city: 'Berlin', tagline: 'Premium European exhibit builder', services: ['Custom Builds', 'AV Integration', 'Transport'], coverage_count: 18, profile_score: 88, proposal_count: 31 },
  { org_id: 'org_c3', name: 'Dubai Expo Builders', country: 'UAE', city: 'Dubai', tagline: 'MENA region specialists for large stands', services: ['Pavilion Design', 'Carpentry', 'Graphics'], coverage_count: 8, profile_score: 79, proposal_count: 22 },
  { org_id: 'org_c4', name: 'Global Expo Group', country: 'US', city: 'New York', tagline: 'Full-service global exhibit solutions', services: ['Turnkey Builds', 'Storage', 'Project Mgmt'], coverage_count: 24, profile_score: 95, proposal_count: 58 },
];

const ExhibitorDashboard = () => {
  const { isMockMode } = useDataMode();
  const { token, user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [limitBannerDismissed, setLimitBannerDismissed] = useState(false);
  const [activityStreak, setActivityStreak] = useState(null);
  const [recommendedContractors, setRecommendedContractors] = useState([]);
  const [aiContractorRecs, setAiContractorRecs] = useState({});
  const [aiContractorLoading, setAiContractorLoading] = useState(false);
  const [showTestimonialForm, setShowTestimonialForm] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 500));
        setData(MOCK_DATA);
        setRecommendedContractors(MOCK_RECOMMENDED);
      } else {
        try {
          const res = await axios.get(`${API}/exhibitor/briefs`, { headers: { Authorization: `Bearer ${token}` } });
          const rawItems = res.data?.items || [];
          // Normalize statuses: OPEN→published, CLOSED→closed, DRAFT→draft
          const briefs = rawItems.map(b => ({
            ...b,
            title: `${b.event?.show || 'Brief'} — Stand Design`,
            status: (b.status || 'DRAFT').toLowerCase().replace('open', 'published'),
            budget_max: b.budget?.max || 0,
            currency: b.budget?.currency || 'EUR',
            deadline: b.deadline_at,
            city: b.event?.city || '',
            proposals_count: b.proposals_count || 0,
          }));
          const proposalsRes = await axios.get(`${API}/exhibitor/proposals`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { items: [] } }));
          const proposals = proposalsRes.data?.items || [];
          setData({
            stats: {
              activeBriefs: briefs.filter(b => b.status === 'published').length,
              receivedProposals: proposals.length,
              awardedProjects: briefs.filter(b => b.status === 'closed').length,
              totalBudget: briefs.reduce((a, b) => a + (b.budget_max || 0), 0),
            },
            recentBriefs: briefs.slice(0, 3),
            recentProposals: proposals.slice(0, 3),
            upcomingFairs: [],
          });
        } catch {
          setError('Could not connect to API. Switch to Mock mode.');
          setData(null);
        }
      }
      setLoading(false);
    };
    load();
  }, [isMockMode, token]);

  // Fetch usage, weekly stats, profile completion in parallel (real mode only)
  useEffect(() => {
    if (isMockMode || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.allSettled([
      axios.get(`${API}/billing/usage`, { headers }),
      axios.get(`${API}/exhibitor/weekly-stats`, { headers }),
      axios.get(`${API}/exhibitor/profile-completion`, { headers }),
      axios.get(`${API}/exhibitor/activity-streak`, { headers }),
    ]).then(([usageRes, weeklyRes, profileRes, streakRes]) => {
      if (usageRes.status === 'fulfilled') setUsageData(usageRes.value.data);
      if (weeklyRes.status === 'fulfilled') setWeeklyStats(weeklyRes.value.data);
      if (profileRes.status === 'fulfilled') setProfileCompletion(profileRes.value.data);
      if (streakRes.status === 'fulfilled') setActivityStreak(streakRes.value.data);
    });
    // Fetch recommended contractors (auth required)
    axios.get(`${API}/exhibitor/recommended-contractors`, { headers })
      .then(r => {
        const items = (r.data?.items || []).slice(0, 4);
        setRecommendedContractors(items);

        // Non-blocking AI scoring of contractors (sessionStorage cached 1h)
        if (items.length > 0) {
          const CACHE_KEY = `ai_contractor_recs_${token?.slice(-8)}`;
          const CACHE_TTL = 60 * 60 * 1000;
          const cached = sessionStorage.getItem(CACHE_KEY);
          if (cached) {
            try {
              const { data: d, ts } = JSON.parse(cached);
              if (Date.now() - ts < CACHE_TTL) { setAiContractorRecs(d); return; }
            } catch { /* ignore */ }
          }
          setAiContractorLoading(true);
          const briefContext = (data?.recentBriefs || []).slice(0, 2).map(b =>
            `Brief: ${b.title || b.id} | Location: ${b.city || 'TBD'} | Budget: ${b.budget_max ? b.budget_max + ' ' + (b.currency || 'EUR') : 'TBD'}`
          ).join('\n') || 'Exhibition stand design and construction needed.';
          axios.post(`${API}/exhibitor/contractors/ai-recommend`,
            { contractor_ids: items.map(c => c.org_id), brief_context: briefContext },
            { headers }
          ).then(res => {
            const recs = res.data?.recommendations || {};
            setAiContractorRecs(recs);
            sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: recs, ts: Date.now() }));
          }).catch(() => {}).finally(() => setAiContractorLoading(false));
        }
      })
      .catch(() => {});
  }, [isMockMode, token]);

  const kpis = data ? [
    { label: t('dashboard.activeBriefs'),    value: data.stats.activeBriefs,     icon: FileText,     color: 'bg-blue-50 text-blue-600',     sub: t('dashboard.publishedRequests') },
    { label: t('dashboard.receivedQuotes'),  value: data.stats.receivedProposals, icon: Inbox,        color: 'bg-blue-50 text-blue-600', sub: t('dashboard.totalReceived') },
    { label: t('dashboard.awardedProjects'), value: data.stats.awardedProjects,   icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-600', sub: t('dashboard.completed') },
    { label: t('dashboard.totalBudget'),     value: `$${(data.stats.totalBudget / 1000).toFixed(0)}K`, icon: DollarSign, color: 'bg-purple-50 text-purple-600', sub: t('dashboard.planned') },
  ] : [];

  if (loading) return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-100 animate-pulse rounded-xl" />)}
      </div>
    </div>
  );

  // Welcome state for new users (real mode, no data yet)
  const isNewUser = !isMockMode && data && data.stats.activeBriefs === 0 && data.stats.receivedProposals === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5 flex items-center gap-1.5">
            {isMockMode
              ? <><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />{t('dashboard.mockData')}</>
              : <><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />{t('dashboard.liveData')}</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => navigate('/find-freelancers')} variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 gap-2 shrink-0">
            <Users className="h-4 w-4" />Freelancer Bul
          </Button>
          <Button onClick={() => navigate('/app/exhibitor/briefs')} className="bg-blue-600 hover:bg-blue-700 text-white gap-2 shrink-0">
            <Plus className="h-4 w-4" />{t('dashboard.newBrief')}
          </Button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>}

      {/* Limit Alert Banner */}
      {!limitBannerDismissed && usageData?.usage?.length > 0 && (() => {
        const criticalItem = usageData.usage.find(u => !u.unlimited && u.limit > 0 && (u.current / u.limit) >= 0.8);
        if (!criticalItem) return null;
        const pct = Math.round((criticalItem.current / criticalItem.limit) * 100);
        const isFull = pct >= 100;
        return (
          <div data-testid="limit-alert-banner" className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${isFull ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
            <AlertTriangle className={`h-5 w-5 shrink-0 ${isFull ? 'text-red-500' : 'text-amber-500'}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${isFull ? 'text-red-800' : 'text-amber-800'}`}>
                {isFull ? `Plan limit reached: ${criticalItem.label}` : `Approaching limit: ${criticalItem.label}`}
              </p>
              <p className={`text-xs mt-0.5 ${isFull ? 'text-red-600' : 'text-amber-600'}`}>
                {criticalItem.current}/{criticalItem.limit} {criticalItem.unit} used ({pct}%). Upgrade to continue.
              </p>
            </div>
            <Button size="sm" onClick={() => navigate('/app/exhibitor/billing')}
              className={`shrink-0 gap-1.5 text-xs h-8 ${isFull ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-500 hover:bg-amber-600'} text-white`}>
              Upgrade <ArrowRight className="h-3 w-3" />
            </Button>
            <button onClick={() => setLimitBannerDismissed(true)} className="shrink-0 text-gray-400 hover:text-gray-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })()}

      {/* Welcome empty state for new users */}
      {isNewUser && (
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 rounded-2xl p-8 text-center">
          <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <FileText className="h-7 w-7 text-blue-500" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{t('dashboard.welcomeReady')}</h3>
          <p className="text-sm text-gray-600 mb-5 max-w-md mx-auto">
            {t('dashboard.welcomeDesc')}
          </p>
          <Button onClick={() => navigate('/app/exhibitor/briefs')} className="bg-blue-600 hover:bg-blue-700 text-white gap-2 px-6">
            <Plus className="h-4 w-4" />{t('dashboard.createFirstBrief')}
          </Button>
        </div>
      )}

      {/* Onboarding CTA — show for first-time exhibitors who haven't done wizard */}
      {profileCompletion && profileCompletion.score < 30 && !localStorage.getItem('exhibitor_onboarding_done') && (
        <div className="flex items-center justify-between gap-4 p-4 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl text-white shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-sm">{t('dashboard.completeExhibitorProfile')}</p>
              <p className="text-xs text-indigo-100 mt-0.5">{t('dashboard.exhibitorOnboardingDesc')}</p>
            </div>
          </div>
          <button onClick={() => navigate('/app/exhibitor/onboarding')}
            className="bg-white text-indigo-700 hover:bg-indigo-50 font-semibold flex-shrink-0 gap-1.5 px-4 py-2 rounded-lg text-sm flex items-center transition-colors"
            data-testid="start-exhibitor-onboarding-btn">
            {t('dashboard.startSetup')} <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <Card key={i} className="border-0 shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{k.label}</p>
                    <p className="text-3xl font-bold text-gray-900 mt-1">{k.value}</p>
                    <p className="text-xs text-gray-400 mt-1">{k.sub}</p>
                  </div>
                  <div className={`p-2.5 rounded-xl ${k.color}`}><Icon className="h-5 w-5" /></div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Weekly Summary + Profile Completeness row */}
      {(weeklyStats || profileCompletion) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Weekly Summary */}
          {weeklyStats && (
            <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50" data-testid="weekly-summary-widget">
              <CardHeader className="pb-2 pt-4 px-5">
                <CardTitle className="text-sm flex items-center gap-2 text-blue-800">
                  <Activity className="h-4 w-4" />{t('dashboard.weeklyActivity')}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: t('dashboard.newBriefs'), value: weeklyStats.new_briefs_7d, icon: FileText, color: 'text-blue-600' },
                    { label: t('dashboard.newQuotes'), value: weeklyStats.new_proposals_7d, icon: Inbox, color: 'text-indigo-600' },
                    { label: t('dashboard.totalQuotes'), value: weeklyStats.total_proposals, icon: TrendingUp, color: 'text-purple-600' },
                  ].map(s => (
                    <div key={s.label} className="text-center">
                      <s.icon className={`h-4 w-4 mx-auto mb-1 ${s.color}`} />
                      <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Profile Completeness */}
          {profileCompletion && (
            <Card className="border-0 shadow-sm" data-testid="profile-completion-widget">
              <CardHeader className="pb-2 pt-4 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2 text-gray-800">
                    <Sparkles className="h-4 w-4 text-amber-500" />{t('dashboard.profileCompleteness')}
                  </CardTitle>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${profileCompletion.score >= 80 ? 'bg-emerald-100 text-emerald-700' : profileCompletion.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-600'}`}>
                    {profileCompletion.score}%
                  </span>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-4">
                <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all duration-500 ${profileCompletion.score >= 80 ? 'bg-emerald-500' : profileCompletion.score >= 50 ? 'bg-amber-400' : 'bg-red-400'}`}
                    style={{ width: `${profileCompletion.score}%` }}
                  />
                </div>
                {profileCompletion.missing_fields.length > 0 ? (
                  <div className="space-y-1">
                    <p className="text-xs text-gray-500 font-medium">{t('dashboard.missing')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profileCompletion.missing_fields.map(f => (
                        <span key={f} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{f}</span>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" />{t('dashboard.profileComplete')}
                  </p>
                )}
                <Button size="sm" variant="outline" onClick={() => {
                    const tabMap = { 'About description': 'overview', 'Company logo': 'overview', 'Cover image': 'overview', 'Upcoming shows': 'upcoming', 'Stand portfolio': 'stands', 'Social links': 'social' };
                    const firstMissing = profileCompletion?.missing_fields?.[0];
                    const tab = tabMap[firstMissing] || 'overview';
                    navigate('/app/exhibitor/profile', { state: { tab } });
                  }}
                  className="mt-3 h-7 text-xs gap-1 w-full border-blue-200 text-blue-600 hover:bg-blue-50">
                  <User2 className="h-3 w-3" />{t('dashboard.completeProfile')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Activity Streak Widget */}
      {activityStreak && (
        <Card className="border-0 shadow-sm" data-testid="exhibitor-activity-streak-widget">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Flame className={`h-5 w-5 ${activityStreak.current_streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} />
                <span className="font-semibold text-sm text-gray-800">{t('dashboard.activityStreak')}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className={`text-2xl font-black ${activityStreak.current_streak > 0 ? 'text-orange-500' : 'text-gray-400'}`}>
                  {activityStreak.current_streak}
                </span>
                <span className="text-xs text-gray-400 font-medium">{t('dashboard.dayStreak')}</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full ml-1">
                  {activityStreak.active_days_30} {t('dashboard.activeDays')}
                </span>
              </div>
            </div>
            <div className="flex gap-1.5">
              {activityStreak.grid.map((day, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <div className={`w-full h-7 rounded-md transition-colors ${day.active ? 'bg-orange-400' : 'bg-gray-100'}`} />
                  <span className="text-[10px] text-gray-400">{day.label}</span>
                </div>
              ))}
            </div>
            {activityStreak.current_streak === 0 && (
              <p className="text-xs text-gray-400 mt-2 text-center">{t('dashboard.streakPrompt')}</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Global Presence Map */}
      <GlobalPresenceWidget token={token} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4 text-blue-500" />{t('dashboard.recentBriefs')}</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => navigate('/app/exhibitor/briefs')}>
                {t('dashboard.viewAll')} <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {(data?.recentBriefs || []).length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">{t('dashboard.noBriefs')}</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {(data?.recentBriefs || []).map(b => {
                  const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.draft;
                  return (
                    <div key={b.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{b.title}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            {b.city && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{b.city}</span>}
                            {b.deadline && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(b.deadline).toLocaleDateString('en-GB')}</span>}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 shrink-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sc.color}`}>{sc.label}</span>
                          {b.proposals > 0 && <span className="text-xs text-gray-500">{b.proposals} quotes</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><Inbox className="h-4 w-4 text-blue-500" />{t('dashboard.recentQuotes')}</CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => navigate('/app/exhibitor/proposals')}>
                {t('dashboard.viewAll')} <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {(data?.recentProposals || []).length === 0 ? (
              <div className="p-8 text-center text-gray-400 text-sm">{t('dashboard.noQuotes')}</div>
            ) : (
              <div className="divide-y divide-gray-50">
                {(data?.recentProposals || []).map(p => {
                  const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
                  return (
                    <div key={p.id} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{p.contractor || p.contractor_name || 'Contractor'}</p>
                          <p className="text-xs text-gray-400 truncate mt-0.5">{p.brief || p.brief_title || p.brief_id}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sc.color}`}>{sc.label}</span>
                          <span className="text-xs font-mono text-gray-700">{p.amount?.toLocaleString()} {p.currency}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {isMockMode && (data?.upcomingFairs || []).length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3 border-b border-gray-50">
            <CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4 text-blue-500" />{t('dashboard.upcomingFairs')}</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-gray-50">
              {data.upcomingFairs.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-4 hover:bg-gray-50">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{f.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{f.location}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs font-medium text-gray-700">{f.date}</span>
                    <Badge className="bg-blue-100 text-blue-700 text-xs">{f.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommended Contractors */}
      {recommendedContractors.length > 0 && (
        <Card className="border-0 shadow-sm" data-testid="recommended-contractors-section">
          <CardHeader className="pb-3 border-b border-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Star className="h-4 w-4 text-amber-500" />
                {t('dashboard.recommendedContractors')}
                {aiContractorLoading && <span className="text-xs text-violet-500 flex items-center gap-1"><Sparkles className="h-3 w-3 animate-pulse" />AI analiz...</span>}
                {!aiContractorLoading && Object.keys(aiContractorRecs).length > 0 && (
                  <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full font-normal flex items-center gap-1">
                    <Sparkles className="h-2.5 w-2.5" />AI Puanlı
                  </span>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs text-gray-500" onClick={() => navigate('/app/exhibitor/find-contractors')}>
                {t('dashboard.browseAll')} <ChevronRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {recommendedContractors.map(c => {
                const aiRec = aiContractorRecs[c.org_id];
                return (
                  <Link
                    key={c.org_id}
                    to={`/contractor/${c.slug || c.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`recommended-contractor-${c.org_id}`}
                    className="bg-gray-50 rounded-xl p-4 hover:bg-blue-50 hover:border-blue-200 border border-transparent transition-all cursor-pointer group block no-underline"
                  >
                    <div className="flex items-center gap-2.5 mb-2.5">
                      <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0 font-bold text-white text-sm shadow-sm">
                        {c.name.charAt(0)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900 truncate group-hover:text-blue-700 transition-colors">{c.name}</p>
                        <p className="text-xs text-gray-500 flex items-center gap-1">
                          <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
                          {c.city && c.country ? `${c.city}, ${c.country}` : c.country || c.city || '—'}
                        </p>
                      </div>
                    </div>
                    {/* AI Score Row */}
                    {aiRec && (
                      <div className="mb-2 flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-violet-600">{'★'.repeat(aiRec.score)}{'☆'.repeat(5 - aiRec.score)}</span>
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                          aiRec.score >= 4 ? 'bg-violet-100 text-violet-700' : 'bg-amber-100 text-amber-700'
                        }`}>{aiRec.score >= 5 ? 'Mükemmel' : aiRec.score >= 4 ? 'Yüksek' : 'Orta'}</span>
                      </div>
                    )}
                    {aiRec?.reason ? (
                      <p className="text-xs text-violet-600 mb-2 line-clamp-1 italic">{aiRec.reason}</p>
                    ) : c.tagline ? (
                      <p className="text-xs text-gray-500 mb-2.5 line-clamp-1 italic">{c.tagline}</p>
                    ) : null}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {(c.services || []).slice(0, 2).map((s, i) => (
                        <span key={i} className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full font-medium">{s}</span>
                      ))}
                      {c.coverage_count > 0 && (
                        <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          <Globe className="h-2.5 w-2.5" />{c.coverage_count}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        <span className="text-[10px] text-gray-500">{c.proposal_count} {t('dashboard.proposals')}</span>
                      </div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${c.profile_score >= 80 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {c.profile_score}%
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deneyimini Paylaş Kartı */}
      {!localStorage.getItem('exhibitor_testimonial_submitted') && (
        <Card className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-amber-50 border border-orange-100">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-xl bg-orange-100 flex items-center justify-center shrink-0">
              <MessageSquare className="h-5 w-5 text-orange-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm">ExpoStand deneyiminizi paylaşın</p>
              <p className="text-xs text-gray-500 mt-0.5">Yorumunuz onaylandıktan sonra ana sayfada yer alabilir.</p>
            </div>
            <Button
              size="sm"
              onClick={() => setShowTestimonialForm(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white shrink-0 gap-1.5"
              data-testid="exhibitor-write-review-btn"
            >
              <Star className="h-3.5 w-3.5" />
              Yorum Yaz
            </Button>
          </CardContent>
        </Card>
      )}

      {showTestimonialForm && (
        <TestimonialForm
          user={user}
          onClose={() => {
            setShowTestimonialForm(false);
            localStorage.setItem('exhibitor_testimonial_submitted', '1');
          }}
        />
      )}
    </div>
  );
};

export default ExhibitorDashboard;
